import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { donationAPI } from '../services/api';
import ProfilePicture from '../components/ProfilePicture';
import { RevealUp, RevealLeft, RevealRight, RevealScale, StaggeredReveal } from '../components/ScrollReveal';

const STATUS_BADGE = {
  completed: { label: "✅ पूर्ण",   bg: "rgba(30,91,71,.1)",   color: "#1E5B47" },
  pending:   { label: "⏳ लंबित",   bg: "rgba(244,165,0,.1)",  color: "#C9860A" },
  failed:    { label: "❌ असफल",    bg: "rgba(220,38,38,.1)",  color: "#DC2626" },
  refunded:  { label: "↩️ वापसी",   bg: "rgba(99,102,241,.1)", color: "#6366F1" },
};

const Dashboard = () => {
  const { user } = useAuth();
  const [donations,   setDonations]   = useState([]);
  const [donStats,    setDonStats]    = useState({ totalAmount:0, totalDonations:0 });
  const [loadingDon,  setLoadingDon]  = useState(true);
  const [page,        setPage]        = useState(1);
  const [totalPages,  setTotalPages]  = useState(1);

  useEffect(() => {
    fetchDonations(page);
  }, [page]);

  const fetchDonations = async (pg) => {
    setLoadingDon(true);
    try {
      const res = await donationAPI.getUserDonations(pg);
      setDonations(res.data.donations || []);
      setDonStats(res.data.stats   || { totalAmount:0, totalDonations:0 });
      setTotalPages(res.data.pagination?.pages || 1);
    } catch (e) {
      console.error('Fetch donations error:', e);
    } finally { setLoadingDon(false); }
  };

  const memberSince = user?.createdAt
    ? new Date(user.createdAt).toLocaleDateString('hi-IN', { year:'numeric', month:'long', day:'numeric' })
    : 'आज';

  const quickActions = [
    { title:'दान करें',           desc:'मंदिर के विकास में योगदान',    icon:'💰', link:'/donate',  grad:'#E8621A,#C44E0E' },
    { title:'प्रोफ़ाइल संपादित', desc:'अपनी जानकारी अपडेट करें',     icon:'✏️',  link:'/profile', grad:'#1460CC,#3B82F6' },
    { title:'गैलरी देखें',        desc:'मंदिर की तस्वीरें',           icon:'🖼️',  link:'/gallery', grad:'#7C3AED,#A78BFA' },
    { title:'संपर्क करें',        desc:'हमसे जुड़ें',                  icon:'📞',  link:'/contact', grad:'#1E5B47,#2D7A60' },
  ];

  return (
    <div className="min-h-screen py-6" style={{ background:'#FEF6E4' }}>
      <div className="max-w-5xl mx-auto px-4 sm:px-5">

        {/* Page Title */}
        <RevealUp className="mb-5">
          <p className="text-xs font-bold uppercase tracking-widest text-temple-primary mb-1">व्यक्तिगत क्षेत्र</p>
          <h1 className="font-serif text-2xl font-bold text-temple-text">डैशबोर्ड</h1>
          <div className="section-divider mt-2" style={{ margin:'0.5rem 0 0' }} />
        </RevealUp>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

          {/* ── LEFT: PROFILE CARD ── */}
          <RevealLeft className="lg:col-span-1">
            <div className="bg-white rounded-3xl shadow-temple border border-temple-secondary/12 overflow-hidden">
              {/* Gradient top */}
              <div className="h-20" style={{ background:'linear-gradient(135deg,#8B1A1A,#C44E0E,#F4A500)' }} />
              <div className="-mt-10 px-6 pb-6">
                <div className="flex justify-center mb-3">
                  <ProfilePicture profilePicture={user?.profilePicture} name={user?.name} size="large" editable={false} />
                </div>
                <div className="text-center mb-5">
                  <h2 className="font-serif text-xl font-bold text-temple-text">{user?.name}</h2>
                  <p className="text-xs text-temple-text-muted break-all">{user?.email}</p>
                  <span className="inline-block mt-2 px-3 py-1 rounded-full text-xs font-bold"
                    style={{ background:'rgba(232,98,26,.1)', color:'#E8621A' }}>
                    {user?.role === 'admin' ? '🛡️ व्यवस्थापक' : '🙏 भक्त'}
                  </span>
                </div>

                {/* Stats row */}
                <div className="grid grid-cols-3 gap-2 mb-5">
                  {[
                    { icon:'💰', label:'कुल दान',    value:`₹${(donStats.totalAmount||0).toLocaleString('hi-IN')}` },
                    { icon:'🎁', label:'बार दान',    value: donStats.totalDonations || 0 },
                    { icon:'📅', label:'सदस्य दिन', value: Math.floor((Date.now()-new Date(user?.createdAt||Date.now()))/(1000*60*60*24)) },
                  ].map(({ icon, label, value }) => (
                    <div key={label} className="text-center p-2 rounded-xl"
                      style={{ background:'rgba(244,165,0,.07)', border:'1px solid rgba(244,165,0,.15)' }}>
                      <div className="text-lg mb-0.5">{icon}</div>
                      <div className="text-sm font-bold text-temple-text">{value}</div>
                      <div className="text-[9px] text-temple-text-muted">{label}</div>
                    </div>
                  ))}
                </div>

                <div className="text-xs text-temple-text-muted text-center mb-4">
                  📅 सदस्य बने: {memberSince}
                </div>

                <Link to="/profile"
                  className="block w-full py-3 text-center rounded-2xl font-bold text-sm text-white"
                  style={{ background:'linear-gradient(135deg,#E8621A,#C44E0E)' }}>
                  ✏️ प्रोफ़ाइल संपादित करें
                </Link>
              </div>
            </div>
          </RevealLeft>

          {/* ── RIGHT: MAIN ── */}
          <div className="lg:col-span-2 space-y-4">

            {/* Quick Actions */}
            <RevealUp>
              <div className="bg-white rounded-3xl shadow-temple border border-temple-secondary/12 p-6">
                <h3 className="font-serif text-lg font-bold text-temple-text mb-4">⚡ त्वरित कार्य</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {quickActions.map(({ title, desc, icon, link, grad }) => (
                    <Link key={link} to={link}
                      className="group flex flex-col items-center gap-2 p-4 rounded-2xl border-2 border-transparent hover:border-temple-secondary/30 transition-all hover:-translate-y-1"
                      style={{ background:'rgba(244,165,0,.04)' }}>
                      <div className="w-11 h-11 rounded-2xl flex items-center justify-center text-xl"
                        style={{ background:`linear-gradient(135deg,${grad})` }}>
                        {icon}
                      </div>
                      <span className="text-xs font-bold text-temple-text text-center leading-tight">{title}</span>
                    </Link>
                  ))}
                </div>
              </div>
            </RevealUp>

            {/* Donation History */}
            <RevealUp delay={150}>
              <div className="bg-white rounded-3xl shadow-temple border border-temple-secondary/12 p-6">
                <div className="flex items-center justify-between mb-5">
                  <h3 className="font-serif text-lg font-bold text-temple-text">💳 दान इतिहास</h3>
                  <Link to="/donate"
                    className="text-xs font-bold px-3 py-1.5 rounded-xl text-white"
                    style={{ background:'linear-gradient(135deg,#E8621A,#C44E0E)' }}>
                    + नया दान
                  </Link>
                </div>

                {loadingDon ? (
                  <div className="flex items-center justify-center py-10">
                    <div className="loader" style={{ borderTopColor:'#E8621A', width:'28px', height:'28px', borderWidth:'3px' }} />
                  </div>
                ) : donations.length === 0 ? (
                  <div className="text-center py-10">
                    <div className="text-5xl mb-3">🙏</div>
                    <p className="text-temple-text-muted text-sm mb-4">अभी तक कोई दान नहीं हुआ।</p>
                    <Link to="/donate"
                      className="inline-block px-6 py-2.5 rounded-2xl font-bold text-sm text-white"
                      style={{ background:'linear-gradient(135deg,#8B1A1A,#C44E0E)' }}>
                      पहला दान करें →
                    </Link>
                  </div>
                ) : (
                  <>
                    <div className="space-y-3">
                      {donations.map((d) => {
                        const s = STATUS_BADGE[d.paymentStatus] || STATUS_BADGE.pending;
                        return (
                          <div key={d._id}
                            className="flex items-center justify-between p-4 rounded-2xl"
                            style={{ background:'rgba(244,165,0,.05)', border:'1px solid rgba(244,165,0,.12)' }}>
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-xl flex items-center justify-center text-lg flex-shrink-0"
                                style={{ background:'rgba(232,98,26,.1)' }}>
                                💰
                              </div>
                              <div>
                                <p className="text-sm font-bold text-temple-text">
                                  ₹{Number(d.amount).toLocaleString('hi-IN')}
                                </p>
                                <p className="text-xs text-temple-text-muted">{d.purpose}</p>
                                <p className="text-[10px] text-temple-text-muted mt-0.5">
                                  {new Date(d.createdAt).toLocaleDateString('hi-IN', { day:'numeric', month:'long', year:'numeric' })}
                                </p>
                              </div>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <span className="inline-block px-2 py-1 rounded-full text-[10px] font-bold"
                                style={{ background: s.bg, color: s.color }}>
                                {s.label}
                              </span>
                              {d.receiptNumber && (
                                <p className="text-[10px] font-mono text-temple-text-muted mt-1">{d.receiptNumber}</p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="flex items-center justify-center gap-3 mt-4">
                        <button disabled={page===1} onClick={() => setPage(p=>p-1)}
                          className="px-4 py-2 rounded-xl text-xs font-bold border-2 border-temple-secondary/20 disabled:opacity-40 hover:border-temple-primary/40 transition-all">
                          ← पिछला
                        </button>
                        <span className="text-xs text-temple-text-muted font-semibold">
                          {page} / {totalPages}
                        </span>
                        <button disabled={page===totalPages} onClick={() => setPage(p=>p+1)}
                          className="px-4 py-2 rounded-xl text-xs font-bold border-2 border-temple-secondary/20 disabled:opacity-40 hover:border-temple-primary/40 transition-all">
                          अगला →
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </RevealUp>

            {/* Welcome Banner */}
            <RevealScale delay={200}>
              <div className="rounded-3xl p-6 text-white text-center relative overflow-hidden"
                style={{ background:'linear-gradient(135deg,#8B1A1A,#C44E0E,#F4A500)' }}>
                <div className="absolute inset-0 opacity-[0.06] flex items-center justify-center">
                  <span style={{ fontSize:'20rem', lineHeight:1 }}>🕉️</span>
                </div>
                <div className="relative">
                  <div className="text-3xl mb-2">🕉️</div>
                  <h3 className="font-serif text-lg font-bold mb-2">फुलहर धाम में आपका स्वागत है</h3>
                  <p className="text-white/75 text-xs mb-4 leading-relaxed max-w-sm mx-auto">
                    आपके सहयोग से मंदिर का विकास संभव है। हर दान पुण्य का काम है।
                  </p>
                  <div className="flex flex-wrap justify-center gap-3">
                    <Link to="/about" className="px-4 py-2 rounded-xl text-xs font-bold bg-white/15 hover:bg-white/25 transition-all">
                      हमारे बारे में
                    </Link>
                    <Link to="/news" className="px-4 py-2 rounded-xl text-xs font-bold bg-white/15 hover:bg-white/25 transition-all">
                      समाचार
                    </Link>
                    <Link to="/gallery" className="px-4 py-2 rounded-xl text-xs font-bold bg-white/15 hover:bg-white/25 transition-all">
                      गैलरी
                    </Link>
                  </div>
                </div>
              </div>
            </RevealScale>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;